import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientApplicationFilterForm,
  ClientApplicationFilterFormRef,
  ClientApplicationFilterFormValues,
} from "@components/Forms/ClientApplicationFilterForm/ClientApplicationFilterForm";
import { ClientApplicationsTable } from "@components/Tables";
import {
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/ClientApplicationsTable/ClientApplicationsTableConfig";
import { ClientApplicationsTableColumnCustomization } from "@components/Tables/ClientApplicationsTable/UI/ClientApplicationsTableColumnCustomization";
import {
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientApplicationsTable/UI/ClientApplicationsTableColumns";
import { useClientApplicationTableExport } from "@components/Tables/ClientApplicationsTable/utils/useClientApplicationTableExport";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";

type Props = {
  clientId: string;
};

export const ClientApplicationsCard = ({ clientId }: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<ClientApplicationFilterFormValues>(getDefaultFilterValues());

  const filterFormRef = useRef<ClientApplicationFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ClientApplicationFilterFormValues) => {
      setFilterValues(newValues);
      // Close the panel when form is submitted (e.g., via Enter key)
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useClientApplicationTableExport({
    tableId: "client-applications-table",
    clientId,
    exportType: "csv",
    reviewState: filterValues.reviewState,
    managedBy: filterValues.managedBy,
    supportedBy: filterValues.supportedBy,
    businessOwnerId: filterValues.businessOwnerId,
    productNameSearch: filterValues.productNameSearch,
    parentCategoryId: filterValues.parentCategoryId,
    createdAt_Gte: filterValues.createdAt_Gte,
    createdAt_Lte: filterValues.createdAt_Lte,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Client applications exported successfully!");
    } catch (error) {
      toast.error("Failed to export client applications");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId="client-applications-table"
      defaultColumnSettings={DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_APPLICATION_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {filtersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={filtersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
          <IconButton
            size="sm"
            icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
            onClick={handleExportToCsv}
            outline={"sm"}
            tooltip={
              isExporting ? "Exporting..." : "Export table data to CSV"
            }
            disabled={isExporting}
          />
        </Box>
      </PortalHeaderActions>
      <ClientApplicationsTable
        clientId={clientId}
        reviewState={
          filterValues.reviewState && filterValues.reviewState.length > 0
            ? filterValues.reviewState
            : undefined
        }
        managedBy={
          filterValues.managedBy && filterValues.managedBy.length > 0
            ? filterValues.managedBy
            : undefined
        }
        supportedBy={
          filterValues.supportedBy && filterValues.supportedBy.length > 0
            ? filterValues.supportedBy
            : undefined
        }
        businessOwnerId={filterValues.businessOwnerId}
        productNameSearch={filterValues.productNameSearch}
        parentCategoryId={filterValues.parentCategoryId}
        createdAt_Gte={filterValues.createdAt_Gte}
        createdAt_Lte={filterValues.createdAt_Lte}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Filters"
      >
        <ClientApplicationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          clientId={clientId}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ClientApplicationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
